import { useEffect, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api, type BodySeries } from "../../lib/api";
import { EmptyState } from "../../components/EmptyState";
import { BodyStat } from "./_shared";

const ACCENT = "#9a3412";
const FAINT = "#d6b8a6";
const RANGES = [
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
  { days: 365, label: "1y" },
  { days: 3650, label: "All" },
];

export default function WeightTrend() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState<BodySeries | null | "error">(null);

  useEffect(() => {
    setData(null);
    api.body.series(days).then(setData).catch(() => setData("error"));
  }, [days]);

  const d = data && data !== "error" ? data : null;
  const has = d != null && d.points.length > 1;
  const rate = d?.rate_kg_per_week ?? null;

  return (
    <div className="animate-fade-in">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="section-title">Weight trend</h2>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              onClick={() => setDays(r.days)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition duration-fast ${
                days === r.days ? "bg-accent/10 text-accent" : "text-ink-muted hover:text-ink"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {!has ? (
        <EmptyState error={data === "error"} title="Not enough weigh-ins yet">
          A couple of weigh-ins and the trend line (with its 7-day smoothing) starts to take shape.
        </EmptyState>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            <BodyStat label="Latest" value={d!.last ? d!.last.weight_kg.toFixed(1) : null} unit="kg" accent />
            <BodyStat label="Rate" value={rate != null ? `${rate > 0 ? "+" : ""}${rate}` : null} unit="kg/week" />
            <BodyStat
              label="Over range"
              value={d!.first && d!.last ? (d!.last.weight_kg - d!.first.weight_kg).toFixed(1) : null}
              unit="kg"
            />
          </div>

          <div className="card card-pad mt-4">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={d!.points}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                <XAxis dataKey="date" axisLine={false} tickLine={false} fontSize={11} minTickGap={28} />
                <YAxis axisLine={false} tickLine={false} width={36} fontSize={11} domain={["auto", "auto"]} />
                <Tooltip formatter={(v: number, n) => [`${v} kg`, n === "avg7" ? "7-day avg" : "Weigh-in"]} />
                <Line type="monotone" dataKey="weight_kg" name="weight" stroke={FAINT} strokeWidth={1.5} dot={false} connectNulls />
                <Line type="monotone" dataKey="avg7" name="avg7" stroke={ACCENT} strokeWidth={2.5} dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
            <p className="mt-2 text-xs text-ink-subtle">
              Faint line = each weigh-in · bold line = 7-day moving average (ignores daily
              water-weight noise — the number to actually watch).
            </p>
          </div>
        </>
      )}
    </div>
  );
}
